import { Box } from "@mui/material"
import React, { memo, useEffect, useMemo, useState } from "react"
import { shallowEqual } from "react-redux"
import type { AnySceneObjectT } from "tweenly"
import { compareAnimations } from "../../../../helpers/animation.helpers"
import { selectUndoable } from "../../../../helpers/selector.helpers"
import { useAppSelector } from "../../../../hooks/useRedux"
import { selectObjectsByIds } from "../../../../store/slices/objects.slice"
import { TIMELINE_ROW } from "../../../../style/sizing"
import TimelineRow from "./TimelineRow"
import TimelineRows from "./TimelineRows"

interface Props {
    object: AnySceneObjectT
    index: number
    timelineRowWidth: number
    isHidingObject: boolean
    isLockingObject: boolean
    rowsExpanded: boolean
    timelineAreaWidth: number
    isParentTicker: boolean
}
const TimelineRowWrapperComponent = ({
    object,
    index,
    timelineRowWidth,
    isHidingObject,
    isLockingObject,
    rowsExpanded,
    timelineAreaWidth,
    isParentTicker,
}: Props) => {
    //[NOTE]: this combination of useEffect and useState is necessary,
    //bcs. rowsExpand sets all objects on same value, but user can expand and collapse each row separately
    const [isExpanded, setIsExpanded] = useState<boolean>(rowsExpanded)
    useEffect(() => {
        setIsExpanded(rowsExpanded)
    }, [rowsExpanded])

    const handleCollapse = () => {
        setIsExpanded(false)
    }
    const handleExpand = () => {
        setIsExpanded(true)
    }

    // only this row's direct children, compared shallowly — a change to any other object leaves
    // these references untouched, so the other rows bail out instead of re-rendering. The whole
    // objects slice used to be passed down as a prop, which re-rendered every row (and its ~20
    // time steps) on every object edit.
    const childObjects = useAppSelector(
        (state) => selectObjectsByIds(selectUndoable(state).objects, object.childIds),
        shallowEqual
    )

    const animationIds = useMemo(
        () => (object.animations ? Object.keys(object.animations) : []),
        [object.animations]
    )
    const animationIdsLength = animationIds.length

    // The comparator used to call selectAnimationById, which walks every object and every
    // animation and allocates an array — inside a .sort(). The animations of THIS row are
    // already on the `object` prop, so no store subscription is needed at all: the global
    // animations map changes on every object edit and would re-render every row.
    const sortedAnimationIds = useMemo(
        () =>
            [...animationIds].sort((a1, a2) =>
                compareAnimations(object.animations[a1], object.animations[a2])
            ),
        [animationIds, object.animations]
    )

    if (isParentTicker) return null
    if (!object || (animationIdsLength === 0 && object.childIds.length === 0))
        return <React.Fragment key={index}></React.Fragment>
    if (animationIdsLength === 0 && object.childIds.length > 0)
        return (
            <TimelineRows
                objects={childObjects}
                isParentTicker={object.type === "ticker"}
                timelineRowWidth={timelineRowWidth}
                isHidingObject={isHidingObject || (object.isHidden ? object.isHidden : false)}
                isLockingObject={isLockingObject || object.isLocked}
            />
        )
    if (object.type !== "sequence" && animationIdsLength > 0)
        return (
            <Box
                key={index}
                sx={{
                    width: { timelineAreaWidth } + "px",
                    marginBottom: TIMELINE_ROW.paddingBottom + "px",
                }}
            >
                {isExpanded ? (
                    sortedAnimationIds.map((animationId: string, index: number) => (
                        <TimelineRow
                            key={animationId}
                            object={object}
                            animationId={animationId}
                            isMainRow={index === 0}
                            timelineRowWidth={timelineRowWidth}
                            isHidingObject={
                                isHidingObject || (object.isHidden ? object.isHidden : false)
                            }
                            isLockingObject={isLockingObject || object.isLocked}
                            isExpanded={isExpanded}
                            handleCollapse={handleCollapse}
                            handleExpand={handleExpand}
                        />
                    ))
                ) : (
                    <TimelineRow
                        object={object}
                        animationId={null}
                        isMainRow={true}
                        timelineRowWidth={timelineRowWidth}
                        isHidingObject={
                            isHidingObject || (object.isHidden ? object.isHidden : false)
                        }
                        isLockingObject={isLockingObject || object.isLocked}
                        isExpanded={isExpanded}
                        handleCollapse={handleCollapse}
                        handleExpand={handleExpand}
                    />
                )}

                {object.childIds.length > 0 && (
                    <TimelineRows
                        objects={childObjects}
                        isParentTicker={object.type === "ticker"}
                        timelineRowWidth={timelineRowWidth}
                        isHidingObject={
                            isHidingObject || (object.isHidden ? object.isHidden : false)
                        }
                        isLockingObject={isLockingObject || object.isLocked}
                    />
                )}
            </Box>
        )
    else
        return (
            <Box
                key={index}
                sx={{
                    width: { timelineAreaWidth } + "px",
                    marginBottom: TIMELINE_ROW.paddingBottom + "px",
                }}
            >
                <TimelineRow
                    object={object}
                    animationId={null}
                    isMainRow={true}
                    timelineRowWidth={timelineRowWidth}
                    isHidingObject={isHidingObject || (object.isHidden ? object.isHidden : false)}
                    isLockingObject={isLockingObject || object.isLocked}
                    isExpanded={isExpanded}
                    handleCollapse={handleCollapse}
                    handleExpand={handleExpand}
                />
                {/* recursive for TimelineRows */}
                {object.childIds.length > 0 && (
                    <TimelineRows
                        objects={childObjects}
                        isParentTicker={object.type === "ticker"}
                        timelineRowWidth={timelineRowWidth}
                        isHidingObject={
                            isHidingObject || (object.isHidden ? object.isHidden : false)
                        }
                        isLockingObject={isLockingObject || object.isLocked}
                    />
                )}
            </Box>
        )
}

export const TimelineRowWrapper = memo(TimelineRowWrapperComponent)
